/**
 * What the terminal says back when an order goes — ROADMAP 4.16.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONLY FEEDBACK A ONE-CLICK FIRE GETS
 *
 * Clicking B or S in the chain sends immediately: no ticket, no dialog, no
 * confirmation. That makes this the whole of the answer, so it says the two
 * things a trader needs within a second of clicking — WHAT went and whether
 * it was ACCEPTED — and nothing else.
 *
 * ACCEPTED IS NOT FILLED, and the wording keeps them apart. The broker
 * acknowledging an order is not the exchange reporting a trade; the drawer
 * is where a fill appears. A toast saying "bought" for an order that is
 * still working would be the product's first lie about money.
 *
 * The bar across the bottom is the time left before it leaves — a timer, not
 * a progress bar for any work. Green for accepted, red for refused, so the
 * outcome is readable from across a desk without reading the sentence.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { toast } from 'sonner'
import { AlertCircle, CheckCircle2, X } from 'lucide-react'

type Tone = 'ok' | 'fail'

const DURATION: Record<Tone, number> = {
  ok: 3200,
  /*
   * A refusal stays longer. It carries a REASON the trader has to read, and
   * it usually arrives while they are looking at the ladder rather than at
   * the corner of the screen.
   */
  fail: 6000,
}

function ToastCard({
  tone,
  title,
  detail,
  onDismiss,
}: {
  tone: Tone
  title: string
  detail?: string | undefined
  onDismiss: () => void
}) {
  const ok = tone === 'ok'
  return (
    <div className="apna-toast" data-testid={`toast-${tone}`} role="status">
      <div className="apna-toast-body">
        {ok ? (
          <CheckCircle2 size={18} className="apna-toast-icon ok" />
        ) : (
          <AlertCircle size={18} className="apna-toast-icon fail" />
        )}
        <div className="apna-toast-text">
          <span className="apna-toast-title">{title}</span>
          {detail && <span className="apna-toast-detail">{detail}</span>}
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="apna-toast-close">
          <X size={14} />
        </button>
      </div>
      <div
        className={`apna-toast-bar ${ok ? 'ok' : 'fail'}`}
        style={{ animationDuration: `${DURATION[tone]}ms` }}
      />
    </div>
  )
}

function show(tone: Tone, title: string, detail?: string): void {
  toast.custom(
    (id) => (
      <ToastCard tone={tone} title={title} detail={detail} onDismiss={() => toast.dismiss(id)} />
    ),
    { duration: DURATION[tone] },
  )
}

/** An order the broker accepted, a save that went through. */
export function notifyOk(title: string, detail?: string): void {
  show('ok', title, detail)
}

/** A refusal, with the reason. */
export function notifyFail(title: string, detail?: string): void {
  show('fail', title, detail)
}
